import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ChatCircleDots,
  HandHeart,
  Warning,
  DeviceMobile,
  Desktop,
  ArrowsClockwise,
  ChartLineUp,
  Path as PathIcon,
} from "@phosphor-icons/react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "all", label: "All time" },
];

const GRANULARITIES = [
  { key: "day", label: "Daily" },
  { key: "week", label: "Weekly" },
  { key: "month", label: "Monthly" },
];

const PATHWAY_LABELS = {
  grief: "Grief & Loss",
  caregiving: "Caregiving Support",
  hospice: "Hospice & End-of-Life",
  stress: "Stress & Burnout",
  prayer: "Prayer Request",
  books: "Books & Resources",
  workshops: "Workshops & Classes",
  ask: "Ask Chaplain Derrick",
  contact: "Contact the Ministry",
  unsure: "Not Sure Where to Start",
  workplace: "Workplace Stress (CCCHAPS)",
};

const CHIP_LABELS = {
  contact: "Contact the Ministry",
  "request-support": "Request Support",
  prayer: "Prayer Request",
  "ask-chaplain": "Ask Chaplain Derrick",
};

const ROUTE_LABELS = {
  "/grief-support": "Grief & Support page",
  "/resources": "Free Resources",
  "/classes": "Classes & Chapel",
  "/request-support": "Request Support",
  "/from-diagnosis-to-goodbye": "From Diagnosis to Goodbye",
  "/books": "Books & Publications",
  "/bulk-orders": "Bulk Orders",
  "/contact": "Contact",
  "/ask-chaplain-derrick": "Ask Chaplain Derrick",
};

// ---------------- Small primitives ----------------
const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div
    className="rounded-2xl border border-ink-100 bg-white p-4 shadow-sm"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-ink-500">
      {Icon && <Icon size={16} weight="duotone" className="text-primary" />}
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">
        {label}
      </span>
    </div>
    <div className="mt-2 font-heading text-2xl font-medium text-ink-900">
      {value}
    </div>
    {sub && <div className="mt-0.5 text-xs text-ink-500">{sub}</div>}
  </div>
);

const Bar = ({ label, count, total, testid }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div
      className="flex items-center gap-3 py-1.5"
      data-testid={testid}
    >
      <div className="w-44 shrink-0 truncate text-sm text-ink-800">
        {label}
      </div>
      <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-softblue">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-primary"
          style={{ width: `${Math.max(4, pct)}%` }}
        />
      </div>
      <div className="w-16 shrink-0 text-right text-sm font-semibold text-ink-800">
        {count}
      </div>
      <div className="w-10 shrink-0 text-right text-xs text-ink-500">{pct}%</div>
    </div>
  );
};

const Sparkline = ({ series, height = 96 }) => {
  if (!series || series.length === 0) {
    return (
      <div className="flex h-24 items-center justify-center text-sm text-ink-500">
        No data yet.
      </div>
    );
  }
  const width = 640;
  const max = Math.max(1, ...series.map((s) => s.opens));
  const stepX = series.length > 1 ? width / (series.length - 1) : 0;
  const points = series
    .map((s, i) => {
      const x = i * stepX;
      const y = height - (s.opens / max) * (height - 12) - 6;
      return `${x},${y}`;
    })
    .join(" ");
  const area = `0,${height} ${points} ${(series.length - 1) * stepX},${height}`;
  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height={height}
        className="text-primary"
      >
        <polygon points={area} fill="currentColor" opacity="0.12" />
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {series.map((s, i) => {
          const x = i * stepX;
          const y = height - (s.opens / max) * (height - 12) - 6;
          return <circle key={s.bucket} cx={x} cy={y} r="3" fill="currentColor" />;
        })}
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-ink-500">
        {series.length > 0 && (
          <>
            <span>{series[0].bucket}</span>
            {series.length > 1 && (
              <span>{series[series.length - 1].bucket}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ---------------- Main component ----------------
const GuideAnalytics = () => {
  const [range, setRange] = useState("30d");
  const [granularity, setGranularity] = useState("day");
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API_BASE}/guide/admin/summary`, {
          params: { range },
          withCredentials: true,
        }),
        axios.get(`${API_BASE}/guide/admin/timeseries`, {
          params: { range, granularity },
          withCredentials: true,
        }),
      ]);
      setSummary(s.data);
      setSeries(t.data.series || []);
    } catch (err) {
      toast.error("Could not load Guide analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [range, granularity]); // eslint-disable-line react-hooks/exhaustive-deps

  const pathwaysSorted = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.pathways || {}).sort((a, b) => b[1] - a[1]);
  }, [summary]);
  const chipsSorted = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.chips || {}).sort((a, b) => b[1] - a[1]);
  }, [summary]);
  const routesSorted = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.top_target_routes || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [summary]);
  const entriesSorted = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.top_entry_pages || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [summary]);

  const t = summary?.totals || {};
  const desktopCount = summary?.devices?.desktop || 0;
  const mobileCount = summary?.devices?.mobile || 0;
  const tabletCount = summary?.devices?.tablet || 0;
  const deviceTotal = desktopCount + mobileCount + tabletCount;
  const pctMobile =
    deviceTotal > 0 ? Math.round(((mobileCount + tabletCount) / deviceTotal) * 100) : 0;
  const engagementRate =
    t.guide_opens > 0
      ? Math.round((t.engaged_sessions / t.guide_opens) * 100)
      : 0;

  return (
    <section data-testid="guide-analytics-section" className="space-y-6">
      {/* Header + controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-medium text-ink-900">
            Begin Again Guide Analytics
          </h2>
          <p className="mt-0.5 text-xs text-ink-500">
            Anonymous, aggregate usage only. No personal or free-text data is stored.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="inline-flex rounded-full border border-ink-200 bg-white p-1"
            data-testid="guide-analytics-range"
          >
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRange(r.key)}
                data-testid={`guide-analytics-range-${r.key}`}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                  range === r.key
                    ? "bg-primary text-white shadow-sm"
                    : "text-ink-600 hover:text-primary"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="guide-analytics-refresh"
            className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:border-primary hover:text-primary"
          >
            <ArrowsClockwise size={12} weight="bold" />
            Refresh
          </button>
        </div>
      </div>

      {/* Top-line stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Stat
          icon={ChatCircleDots}
          label="Guide opens"
          value={t.guide_opens || 0}
          testid="ga-stat-opens"
        />
        <Stat
          icon={HandHeart}
          label="Engaged sessions"
          value={t.engaged_sessions || 0}
          sub={t.guide_opens > 0 ? `${engagementRate}% of opens` : null}
          testid="ga-stat-engaged"
        />
        <Stat
          icon={PathIcon}
          label="Pathway selects"
          value={t.pathway_selects || 0}
          testid="ga-stat-pathways"
        />
        <Stat
          icon={HandHeart}
          label="Human handoffs"
          value={t.human_chip_clicks || 0}
          testid="ga-stat-handoffs"
        />
        <Stat
          icon={pctMobile > 50 ? DeviceMobile : Desktop}
          label="Mobile share"
          value={`${pctMobile}%`}
          sub={`${mobileCount + tabletCount}/${deviceTotal || 0} sessions`}
          testid="ga-stat-mobile"
        />
        <Stat
          icon={Warning}
          label="Safety triggers"
          value={t.safety_pathway_triggered || 0}
          testid="ga-stat-safety"
        />
      </div>

      {/* Warm Welcome — one-line summary row */}
      <div className="rounded-2xl border border-ink-100 bg-white p-4 shadow-sm" data-testid="ga-warm-welcome">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-ink-500">
            <HandHeart size={16} weight="duotone" className="text-primary" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">
              Warm Welcome invitation
            </span>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <span data-testid="ga-ww-shown">
              <span className="text-ink-500">Shown</span>{" "}
              <span className="font-semibold text-ink-900">{t.warm_welcome_shown || 0}</span>
            </span>
            <span data-testid="ga-ww-opened">
              <span className="text-ink-500">Opened Guide</span>{" "}
              <span className="font-semibold text-ink-900">{t.warm_welcome_opened || 0}</span>
              {t.warm_welcome_shown > 0 && (
                <span className="ml-1 text-ink-500">
                  ({Math.round(((t.warm_welcome_opened || 0) / t.warm_welcome_shown) * 100)}%)
                </span>
              )}
            </span>
            <span data-testid="ga-ww-dismissed">
              <span className="text-ink-500">Dismissed</span>{" "}
              <span className="font-semibold text-ink-900">{t.warm_welcome_dismissed || 0}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Trend chart */}
      <div className="rounded-2xl border border-ink-100 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ChartLineUp size={16} weight="duotone" className="text-primary" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
              Trend — Guide opens
            </span>
          </div>
          <div
            className="inline-flex rounded-full border border-ink-200 bg-white p-1"
            data-testid="guide-analytics-granularity"
          >
            {GRANULARITIES.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => setGranularity(g.key)}
                data-testid={`guide-analytics-granularity-${g.key}`}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-all ${
                  granularity === g.key
                    ? "bg-primary text-white shadow-sm"
                    : "text-ink-600 hover:text-primary"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <Sparkline series={series} />
      </div>

      {/* Two-column breakdowns */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Pathways */}
        <div className="rounded-2xl border border-ink-100 bg-white p-4 shadow-sm">
          <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
            Primary pathway selections
          </div>
          {pathwaysSorted.length === 0 ? (
            <div className="py-2 text-sm text-ink-500">No selections in this range.</div>
          ) : (
            <div data-testid="ga-pathways-list">
              {pathwaysSorted.map(([key, count]) => (
                <Bar
                  key={key}
                  label={PATHWAY_LABELS[key] || key}
                  count={count}
                  total={t.pathway_selects || 0}
                  testid={`ga-pathway-${key}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Human chips */}
        <div className="rounded-2xl border border-ink-100 bg-white p-4 shadow-sm">
          <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
            Human-support handoffs
          </div>
          {chipsSorted.length === 0 ? (
            <div className="py-2 text-sm text-ink-500">No handoff clicks in this range.</div>
          ) : (
            <div data-testid="ga-chips-list">
              {chipsSorted.map(([key, count]) => (
                <Bar
                  key={key}
                  label={CHIP_LABELS[key] || key}
                  count={count}
                  total={t.human_chip_clicks || 0}
                  testid={`ga-chip-${key}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Top target routes */}
        <div className="rounded-2xl border border-ink-100 bg-white p-4 shadow-sm">
          <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
            Top page click-throughs
          </div>
          {routesSorted.length === 0 ? (
            <div className="py-2 text-sm text-ink-500">No click-throughs yet.</div>
          ) : (
            <div data-testid="ga-routes-list">
              {routesSorted.map(([key, count]) => (
                <Bar
                  key={key}
                  label={ROUTE_LABELS[key] || key}
                  count={count}
                  total={t.route_click_throughs || 0}
                  testid={`ga-route-${key.replace(/\W+/g, "-")}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Entry pages */}
        <div className="rounded-2xl border border-ink-100 bg-white p-4 shadow-sm">
          <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
            Where visitors opened the Guide
          </div>
          {entriesSorted.length === 0 ? (
            <div className="py-2 text-sm text-ink-500">No opens yet.</div>
          ) : (
            <div data-testid="ga-entries-list">
              {entriesSorted.map(([key, count]) => (
                <Bar
                  key={key}
                  label={key}
                  count={count}
                  total={t.guide_opens || 0}
                  testid={`ga-entry-${key.replace(/\W+/g, "-")}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Small footer */}
      <div className="rounded-2xl border border-ink-100 bg-softblue/40 p-3 text-xs text-ink-600">
        <span className="font-semibold text-ink-800">Privacy:</span> Only
        anonymous, categorical events are stored (event type, pathway, device,
        page). No free-text messages, names, emails, phone numbers, or IP
        addresses are recorded. The anonymous session id is generated in the
        browser tab and cleared when the tab closes.
      </div>

      {loading && (
        <div
          className="fixed bottom-4 left-4 rounded-full bg-primary px-3 py-1.5 text-xs text-white shadow-lg"
          data-testid="guide-analytics-loading"
        >
          Loading…
        </div>
      )}
    </section>
  );
};

export default GuideAnalytics;
